import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useMaxWidth } from '../../hooks/useMaxWidth';
import { formatDateOnlyInputText, parseDateOnlyInput } from '../../utils/dateTime';

type DateOnlyFieldProps = {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Show “type or pick” hint under the field. Default false for compact forms. */
  showHint?: boolean;
};

/** CRM-style date field: typed DD/MM/YYYY text + calendar button (no time). */
export function DateOnlyField({
  value,
  onChange,
  required,
  disabled,
  id,
  showHint = false,
}: DateOnlyFieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const pickerRef = useRef<HTMLInputElement>(null);
  const isMobilePicker = useMaxWidth(900);
  const [text, setText] = useState(() => formatDateOnlyInputText(value));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setText(formatDateOnlyInputText(value));
  }, [value, editing]);

  const commitText = useCallback(() => {
    const parsed = parseDateOnlyInput(text);
    if (parsed) {
      onChange(parsed);
      setText(formatDateOnlyInputText(parsed));
    } else {
      setText(formatDateOnlyInputText(value));
    }
    setEditing(false);
  }, [text, value, onChange]);

  const openPicker = () => {
    if (disabled) return;
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // Safari may throw when not triggered from a direct gesture; fall back to click.
      }
    }
    el.click();
  };

  return (
    <div className={`task-due-field${isMobilePicker ? ' task-due-field--mobile-picker' : ''}`}>
      <input
        id={fieldId}
        className="field-input task-due-field__text"
        type="text"
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setEditing(true);
          setText(e.target.value);
        }}
        onBlur={commitText}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="e.g. 22/09/2026"
        required={required}
        autoComplete="off"
        inputMode="numeric"
      />
      <div className="task-due-field__picker-wrap">
        {isMobilePicker ? (
          <>
            <span className="task-due-field__picker-btn" aria-hidden="true">
              <span className="task-due-field__picker-icon">📅</span>
            </span>
            <input
              ref={pickerRef}
              className="task-due-field__picker-native"
              type="date"
              value={value}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              aria-label="Pick date"
            />
          </>
        ) : (
          <>
            <button
              type="button"
              className="task-due-field__picker-btn"
              onClick={openPicker}
              disabled={disabled}
              aria-label="Open date picker"
              title="Pick date"
            >
              <span className="task-due-field__picker-icon" aria-hidden="true">
                📅
              </span>
            </button>
            <input
              ref={pickerRef}
              className="task-due-field__picker-native"
              type="date"
              value={value}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
            />
          </>
        )}
      </div>
      {showHint ? (
        <p className="field-hint task-due-field__hint">Type to edit, or tap the calendar to pick.</p>
      ) : null}
    </div>
  );
}
